import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { Heading, Field, Flex } from '@oliasoft-open-source/react-ui-library';
import { updateCategory } from 'store/entities/cost-setup/cost-setup';
import { getResolver } from 'src/validation/resolver';
import translations from 'src/internationalisation/translation-map.json';
import { categoriesSchemaValidator } from 'schemas/ajv-validators';
import { Input, Select } from 'client/common/form-inputs';
import { useAutoSave } from 'client/common/auto-save/use-auto-save';
import { autoSaveWaitShort } from 'src/config/config';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import {
  selectCostCodesCategoryOptions,
  selectCostCodesCodeAndNameToIdMap,
  selectCostCodesIdToCodeAndNameMap,
} from 'src/store/entities/cost-codes/selectors';
import { mapNameToCode, validateValue } from './helpers/check-for-valid-id';

const CostCategoryForm = ({
  projectId,
  category,
  updateCategory,
  isPageDisabled,
  costCodesCategories,
  costCodesIdToCodeAndNameMap,
  costCodesCodeAndNameToIdMap,
}) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      costCategoryId: category?.costCategoryId || null,
      name: category?.name || '',
      parentId: null,
      costCodesId: category?.costCodesId || null,
    },
    resolver: getResolver(categoriesSchemaValidator),
  });

  useEffect(() => {
    const categoryData = category;
    reset({
      ...categoryData,
      costCodesId: validateValue(categoryData?.costCodesId, costCodesCategories, costCodesIdToCodeAndNameMap),
    });
  }, [category?.costCategoryId]);

  const debounceUpdateCategory = useRef(debounce(updateCategory, autoSaveWaitShort));
  const onSubmit = handleSubmit((data) => {
    data.projectId = projectId;
    const mappedCostCodesId = mapNameToCode(data.costCodesId, costCodesCodeAndNameToIdMap);
    data.costCodesId = mappedCostCodesId ?? null;
    debounceUpdateCategory.current(data);
  });
  useAutoSave(onSubmit, watch);

  return (
    <>
      <Heading top>{t(translations.categoryDetails)}</Heading>
      <form>
        <Flex gap>
          <Field label={t(translations.name)}>
            <Input
              testId="cost-form-input-name"
              name="name"
              control={control}
              errors={errors}
              width="258px"
              disabled={isPageDisabled}
            />
          </Field>
          <Field label={t(translations.code)}>
            <Select
              testId="cost-form-select-code"
              name="costCodesId"
              control={control}
              errors={errors}
              options={costCodesCategories}
              width="300px"
              disabled={isPageDisabled || category?.parentId}
              clearable
            />
          </Field>
        </Flex>
      </form>
    </>
  );
};

const mapStateToProps = ({ entities }) => ({
  costCodesCategories: selectCostCodesCategoryOptions({ entities }),
  costCodesIdToCodeAndNameMap: selectCostCodesIdToCodeAndNameMap({ entities }),
  costCodesCodeAndNameToIdMap: selectCostCodesCodeAndNameToIdMap({
    entities,
  }),
});

const mapDispatchToProps = { updateCategory };

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(CostCategoryForm));

export { Container as CostCategoryForm };
